import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Space, message, Tag, Popconfirm, Modal, Descriptions, Divider, Form, Input, Select, DatePicker, Row, Col } from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined, PrinterOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { api } from '../services/api';
import { useCanDelete } from '../hooks/useCanDelete';

interface Document {
  id: number;
  documentNo: string;
  documentDate: string;
  documentTypeName: string;
  warehouseName?: string;
  accountName?: string;
  vehiclePlate?: string | null;
  status: string;
  totalAmount: number;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  currencyId?: number;
  currencyCode?: string;
  currencySymbol?: string;
  exchangeRate?: number;
}

interface DocumentItem {
  lineNo: number;
  productCode: string;
  productName: string;
  variantSku?: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  discountRate: number;
  discountAmount: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  unitPriceEx?: number;
}

interface DocumentDetail extends Document {
  totalTax: number;
  items: DocumentItem[];
}

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isDefault?: boolean;
}

interface DocumentTypeOption {
  id: number;
  typeCode: string;
  typeName: string;
}

const Documents: React.FC = () => {
  const canDelete = useCanDelete();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currencyMap, setCurrencyMap] = useState<Record<number, Currency>>({});
  const [baseCurrencyCode, setBaseCurrencyCode] = useState<string>('TRY');
  const [baseCurrencySymbol, setBaseCurrencySymbol] = useState<string>('₺');
  const printRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [filterDocumentTypeId, setFilterDocumentTypeId] = useState<number | undefined>();
  const [filterAccountName, setFilterAccountName] = useState<string>('');
  const [filterDateRange, setFilterDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeOption[]>([]);
  const [filterForm] = Form.useForm();

  useEffect(() => {
    loadDocuments();
    loadCurrencies();
    loadDocumentTypes();
  }, []);

  const loadDocumentTypes = async () => {
    try {
      const { data } = await api.get<DocumentTypeOption[]>('/documenttypes', { params: { stockOnly: true } });
      setDocumentTypes(data ?? []);
    } catch (error) {
      console.error('Document types load error:', error);
    }
  };

  const loadDocuments = async (overrides?: {
    documentTypeId?: number;
    accountName?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      setLoading(true);
      const docTypeId = overrides?.documentTypeId !== undefined ? overrides.documentTypeId : filterDocumentTypeId;
      const accName = overrides?.accountName !== undefined ? overrides.accountName : filterAccountName?.trim();
      const from = overrides?.dateFrom ?? filterDateRange?.[0]?.format('YYYY-MM-DD');
      const to = overrides?.dateTo ?? filterDateRange?.[1]?.format('YYYY-MM-DD');
      const params: Record<string, string | number | undefined> = {};
      if (docTypeId != null) params.documentTypeId = docTypeId;
      if (accName) params.accountName = accName;
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
      const { data } = await api.get('/documents', { params });
      setDocuments(data);
      if (overrides) {
        setFilterDocumentTypeId(overrides.documentTypeId);
        setFilterAccountName(overrides.accountName ?? '');
        setFilterDateRange(
          overrides.dateFrom && overrides.dateTo
            ? [dayjs(overrides.dateFrom), dayjs(overrides.dateTo)]
            : null
        );
      }
    } catch (error) {
      message.error('Fişler yüklenirken hata oluştu');
      console.error('Documents load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    const v = filterForm.getFieldsValue();
    loadDocuments({
      documentTypeId: v.documentTypeId,
      accountName: v.accountName?.trim(),
      dateFrom: v.dateRange?.[0]?.format('YYYY-MM-DD'),
      dateTo: v.dateRange?.[1]?.format('YYYY-MM-DD'),
    });
  };

  const handleClearFilter = () => {
    filterForm.resetFields();
    setFilterDocumentTypeId(undefined);
    setFilterAccountName('');
    setFilterDateRange(null);
    loadDocuments({});
  };

const loadCurrencies = async () => {
  try {
    const { data } = await api.get<{ baseCurrencyCode: string; items: Currency[] }>('/currencies');
    if (data?.items) {
      const map: Record<number, Currency> = {};
      data.items.forEach(currency => {
        map[currency.id] = currency;
      });
      setCurrencyMap(map);
      const baseCode = data.baseCurrencyCode || 'TRY';
      setBaseCurrencyCode(baseCode);
      const baseCurrency = data.items.find(c => c.code === baseCode) || data.items.find(c => c.isDefault);
      setBaseCurrencySymbol(baseCurrency?.symbol || '₺');
    }
  } catch (error) {
    console.error('Para birimleri yüklenirken hata oluştu:', error);
  }
};

const formatMoney = (amount: number = 0, currencyCode?: string, currencySymbol?: string) => {
  const code = currencyCode || 'TRY';
  const symbol = currencySymbol || '₺';
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: code
    }).format(amount || 0);
  } catch {
    const value = Number.isFinite(amount) ? amount : 0;
    return `${symbol} ${value.toFixed(2)}`;
  }
};

const formatBaseMoney = (amount: number = 0) => formatMoney(amount, baseCurrencyCode, baseCurrencySymbol);

const formatDocumentCurrency = (amount: number = 0, doc?: Document | DocumentDetail | null) =>
  formatMoney(amount, doc?.currencyCode || baseCurrencyCode, doc?.currencySymbol || baseCurrencySymbol);

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/documents/${id}`);
      message.success('Fiş silindi');
      loadDocuments();
    } catch (error) {
      message.error('Fiş silinirken hata oluştu');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      setDetailModalVisible(true);
      const { data } = await api.get(`/documents/${id}`);
      setSelectedDocument(data);
    } catch (error) {
      message.error('Fiş detayı yüklenirken hata oluştu');
      console.error('Detail load error:', error);
      setDetailModalVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Approved': return 'green';
      case 'Cancelled': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Draft': return 'Taslak';
      case 'Approved': return 'Onaylandı';
      case 'Cancelled': return 'İptal';
      default: return status;
    }
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Fiş No',
      dataIndex: 'documentNo',
      key: 'documentNo',
      width: 120,
    },
    {
      title: 'Tarih',
      dataIndex: 'documentDate',
      key: 'documentDate',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'Fiş Tipi',
      dataIndex: 'documentTypeName',
      key: 'documentTypeName',
      width: 150,
    },
    {
      title: 'Depo',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      render: (text: string) => text || '-'
    },
    {
      title: 'Cari',
      dataIndex: 'accountName',
      key: 'accountName',
      render: (text: string, record: Document) => (
        <div>
          <div>{text || '-'}</div>
          {record.vehiclePlate && (
            <div style={{ fontSize: 12, color: '#666' }}>{record.vehiclePlate}</div>
          )}
        </div>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Tutar',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      render: (_: number, record: Document) => {
        const baseAmountFormatted = formatBaseMoney(record.totalAmount);
        const docCurrencyDiffers =
          !!record.currencyCode && record.currencyCode !== baseCurrencyCode && !!record.exchangeRate && record.exchangeRate > 0;
        let originalAmountFormatted: string | null = null;

        if (docCurrencyDiffers && record.exchangeRate) {
          const originalAmount = record.totalAmount / record.exchangeRate;
          originalAmountFormatted = formatDocumentCurrency(originalAmount, record);
        }

        return (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 'bold' }}>{baseAmountFormatted}</div>
            {docCurrencyDiffers && (
              <>
                {originalAmountFormatted && (
                  <div style={{ fontSize: 12, color: '#888' }}>
                    ≈ {originalAmountFormatted}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#9a9a9a' }}>
                  1 {record.currencyCode} = {record.exchangeRate} {baseCurrencyCode}
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: 'Açıklama',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          />
          {canDelete && (
            <Popconfirm
              title="Bu fişi silmek istediğinizden emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
              placement="left"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const itemColumns: ColumnsType<DocumentItem> = [
    {
      title: 'Sıra',
      dataIndex: 'lineNo',
      key: 'lineNo',
      width: 60,
    },
    {
      title: 'Ürün Kodu',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120,
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Varyant',
      dataIndex: 'variantSku',
      key: 'variantSku',
      render: (text: string) => text || '-',
    },
    {
      title: 'Birim',
      dataIndex: 'unitName',
      key: 'unitName',
      width: 80,
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (value: number) => value.toLocaleString('tr-TR', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Birim Fiyat',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right',
      render: (value: number, record: DocumentItem) => {
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        return (
          <div>
            <div>{baseValue}</div>
            {showOriginal && record.unitPriceEx && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {formatDocumentCurrency(record.unitPriceEx, selectedDocument)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'İskonto',
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      width: 100,
      align: 'right',
      render: (value: number) => {
        if (!value || value === 0) return '-';
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        const originalValue = showOriginal ? formatDocumentCurrency(value / (selectedDocument?.exchangeRate || 1), selectedDocument) : null;

        return (
          <div>
            <div>{baseValue}</div>
            {originalValue && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {originalValue}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'KDV',
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      width: 100,
      align: 'right',
      render: (value: number) => {
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        const originalValue = showOriginal ? formatDocumentCurrency(value / (selectedDocument?.exchangeRate || 1), selectedDocument) : null;
        return (
          <div>
            <div>{baseValue}</div>
            {originalValue && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {originalValue}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tutar',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 120,
      align: 'right',
      render: (value: number) => {
        const baseValue = formatBaseMoney(value);
        const showOriginal = selectedDocument &&
          selectedDocument.currencyCode !== baseCurrencyCode &&
          !!selectedDocument.exchangeRate &&
          selectedDocument.exchangeRate > 0;
        const originalValue = showOriginal ? formatDocumentCurrency(value / (selectedDocument?.exchangeRate || 1), selectedDocument) : null;
        return (
          <div style={{ fontWeight: 'bold' }}>
            <div>{baseValue}</div>
            {originalValue && (
              <div style={{ fontSize: 11, color: '#888' }}>
                ≈ {originalValue}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const rangePickerLocale = undefined;

  return (
    <>
    <Card size="small" style={{ marginBottom: 16 }}>
      <Form form={filterForm} layout="vertical" onFinish={handleFilter}>
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="documentTypeId" label="Fiş tipi">
              <Select
                placeholder="Tümü"
                allowClear
                showSearch
                optionFilterProp="label"
                options={documentTypes.map((dt) => ({ value: dt.id, label: dt.typeName }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="accountName" label="Cari adı">
              <Input placeholder="Cari adı ile ara" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="dateRange" label="Tarih aralığı">
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                placeholder={['Başlangıç', 'Bitiş']}
                locale={rangePickerLocale}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Form.Item label=" ">
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                  Filtrele
                </Button>
              </Form.Item>
              <Form.Item label=" ">
                <Button icon={<ClearOutlined />} onClick={handleClearFilter}>
                  Temizle
                </Button>
              </Form.Item>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>

    <Card
      title="Fişler"
      extra={
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => navigate('/documents/add')}
        >
          Yeni Fiş
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={documents}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
        scroll={{ x: 1200 }}
      />
    </Card>

      <Modal
        title="Fiş Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={1200}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
            Yazdır
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>,
        ]}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>Yükleniyor...</div>
        ) : selectedDocument ? (
          <div ref={printRef}>
            <style>{`
              @media print {
                @page {
                  size: A4 landscape;
                  margin: 15mm;
                }
                body * {
                  visibility: hidden;
                }
                #print-content, #print-content * {
                  visibility: visible;
                }
                #print-content {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                }
                .no-print {
                  display: none !important;
                }
                .ant-table-wrapper {
                  overflow: visible !important;
                }
                .ant-table {
                  overflow: visible !important;
                  font-size: 11px !important;
                }
                .ant-table-container {
                  overflow: visible !important;
                }
                .ant-table-body {
                  overflow: visible !important;
                }
                .ant-table-content {
                  overflow: visible !important;
                }
                .print-table table {
                  width: 100% !important;
                  table-layout: auto !important;
                }
                .print-table td, .print-table th {
                  white-space: nowrap !important;
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                  padding: 4px 6px !important;
                  font-size: 10px !important;
                }
                .print-table td:nth-child(1), .print-table th:nth-child(1) { width: 5% !important; }
                .print-table td:nth-child(2), .print-table th:nth-child(2) { width: 10% !important; }
                .print-table td:nth-child(3), .print-table th:nth-child(3) { width: 20% !important; white-space: normal !important; }
                .print-table td:nth-child(4), .print-table th:nth-child(4) { width: 10% !important; }
                .print-table td:nth-child(5), .print-table th:nth-child(5) { width: 8% !important; }
                .print-table td:nth-child(6), .print-table th:nth-child(6) { width: 10% !important; }
                .print-table td:nth-child(7), .print-table th:nth-child(7) { width: 12% !important; }
                .print-table td:nth-child(8), .print-table th:nth-child(8) { width: 10% !important; }
                .print-table td:nth-child(9), .print-table th:nth-child(9) { width: 10% !important; }
                .print-table td:nth-child(10), .print-table th:nth-child(10) { width: 12% !important; }
                .ant-descriptions {
                  font-size: 11px !important;
                }
                .ant-descriptions-item-label,
                .ant-descriptions-item-content {
                  padding: 8px !important;
                  font-size: 11px !important;
                }
              }
            `}</style>
            <div id="print-content">
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0 }}>{selectedDocument.documentTypeName}</h2>
                <p style={{ margin: '5px 0', color: '#666' }}>Fiş No: {selectedDocument.documentNo}</p>
              </div>

              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Fiş No">{selectedDocument.documentNo}</Descriptions.Item>
                <Descriptions.Item label="Tarih">
                  {new Date(selectedDocument.documentDate).toLocaleDateString('tr-TR')}
                </Descriptions.Item>
                <Descriptions.Item label="Fiş Tipi">{selectedDocument.documentTypeName}</Descriptions.Item>
                <Descriptions.Item label="Durum">
                  <Tag color={getStatusColor(selectedDocument.status)}>
                    {getStatusText(selectedDocument.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Depo">{selectedDocument.warehouseName || '-'}</Descriptions.Item>
                <Descriptions.Item label="Cari">
                  <div>{selectedDocument.accountName || '-'}</div>
                  {selectedDocument.vehiclePlate && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{selectedDocument.vehiclePlate}</div>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Para Birimi">
                  {selectedDocument.currencySymbol || baseCurrencySymbol} {selectedDocument.currencyCode || baseCurrencyCode}
                  {selectedDocument.currencyCode && selectedDocument.currencyCode !== baseCurrencyCode && selectedDocument.exchangeRate && (
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      1 {selectedDocument.currencyCode} = {selectedDocument.exchangeRate} {baseCurrencyCode}
                    </div>
                  )}
                </Descriptions.Item>
              <Descriptions.Item label="Genel Toplam (KDV Hariç)">
                {(() => {
                  const baseSubTotal = (selectedDocument.totalAmount || 0) - (selectedDocument.totalTax || 0);
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalSubTotal = showOriginal
                    ? baseSubTotal / (selectedDocument.exchangeRate || 1)
                    : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseSubTotal)}</strong>
                      {showOriginal && originalSubTotal !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalSubTotal, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Sivisiz Toplam">
                {(() => {
                  const baseAmount = (selectedDocument.totalAmount || 0) - (selectedDocument.totalTax || 0);
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalAmount = showOriginal ? baseAmount / (selectedDocument.exchangeRate || 1) : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseAmount)}</strong>
                      {showOriginal && originalAmount !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalAmount, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="KDV Toplamı">
                {(() => {
                  const baseTax = selectedDocument.totalTax || 0;
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalTax = showOriginal
                    ? baseTax / (selectedDocument.exchangeRate || 1)
                    : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseTax)}</strong>
                      {showOriginal && originalTax !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalTax, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Genel Toplam (KDV Dahil)">
                {(() => {
                  const baseTotal = selectedDocument.totalAmount || 0;
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalTotal = showOriginal
                    ? baseTotal / (selectedDocument.exchangeRate || 1)
                    : null;
                  return (
                    <div>
                      <strong>{formatBaseMoney(baseTotal)}</strong>
                      {showOriginal && originalTotal !== null && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          ≈ {formatDocumentCurrency(originalTotal, selectedDocument)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
                {selectedDocument.notes && (
                  <Descriptions.Item label="Açıklama" span={2}>{selectedDocument.notes}</Descriptions.Item>
                )}
              </Descriptions>

              <Divider>Fiş Kalemleri</Divider>

              <Table
                columns={itemColumns}
                dataSource={selectedDocument.items}
                rowKey="lineNo"
                pagination={false}
                size="small"
                className="print-table"
                summary={(pageData) => {
                  const totalAmount = selectedDocument.totalAmount;
                  const totalTax = selectedDocument.totalTax;
                  const subTotal = totalAmount - totalTax;
                  const showOriginal = selectedDocument.currencyCode !== baseCurrencyCode &&
                    !!selectedDocument.exchangeRate && selectedDocument.exchangeRate > 0;
                  const originalSubTotal = showOriginal ? subTotal / (selectedDocument.exchangeRate || 1) : null;
                  const originalTax = showOriginal ? totalTax / (selectedDocument.exchangeRate || 1) : null;
                  const originalTotal = showOriginal ? totalAmount / (selectedDocument.exchangeRate || 1) : null;

                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={9} align="right">
                          <strong>Ara Toplam:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <div>
                            <strong>{formatBaseMoney(subTotal)}</strong>
                            {showOriginal && originalSubTotal !== null && (
                              <div style={{ fontSize: 11, color: '#888' }}>
                                ≈ {formatDocumentCurrency(originalSubTotal, selectedDocument)}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={9} align="right">
                          <strong>KDV:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <div>
                            <strong>{formatBaseMoney(totalTax)}</strong>
                            {showOriginal && originalTax !== null && (
                              <div style={{ fontSize: 11, color: '#888' }}>
                                ≈ {formatDocumentCurrency(originalTax, selectedDocument)}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={9} align="right">
                          <strong style={{ fontSize: 16 }}>GENEL TOPLAM:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <div>
                            <strong style={{ fontSize: 16, color: '#1890ff' }}>
                              {formatBaseMoney(totalAmount)}
                            </strong>
                            {showOriginal && originalTotal !== null && (
                              <div style={{ fontSize: 12, color: '#888' }}>
                                ≈ {formatDocumentCurrency(originalTotal, selectedDocument)}
                              </div>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  );
                }}
              />
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default Documents;
